import {Navigate, Route, Routes, useLocation} from 'react-router-dom';
import Tos from './legal/Tos';
import CookiePolicy from './legal/CookiePolicy';
import PrivacyPolicy from './legal/PrivacyPolicy';
import Imprint from './legal/Imprint';
import LandingPage from './landing-page/LandingPage';
import NotFound from './NotFound';
import ScrollToTop from './utils/ScrollToTop';
import VerifyAndCompleteProfile from './user/VerifyAndCompleteProfile';
import NavbarWeb from './navbar/NavbarWeb';
import NavbarMobile from './navbar/NavbarMobile';
import NavbarMobileBottom from './navbar/NavbarMobileBottom';
import {useSelector} from 'react-redux';
import {RootState} from '../store';
import Footer from './footer/Footer';
import NavBarNoAuthWeb from './navbar/NavBarNoAuthWeb';
import NavBarNoAuthMobile from './navbar/NavBarNoAuthMobile';
import SystemLayout from './dashboard/system/SystemLayout';
import EditProfile from './dashboard/profile/EditProfile';
import Notification from './dashboard/profile/Notification';
import Security from './dashboard/profile/Security';
import Dashboard from './dashboard/profile/dashboard/Dashboard';
import ProfileLayout from './dashboard/profile/ProfileLayout';
import SystemTransactions from './dashboard/system/transactions/SystemTransactions';
import UserTransactions from './dashboard/profile/transactions/UserTransactions';
import Jobs from './dashboard/system/Jobs';
import Settings from './dashboard/system/settings/Settings';
import UserList from './dashboard/system/UserList';
import MfaVerificationForm from './user/MfaVerificationForm';
import StripeOnboarding from './user/stripe-onboarding/StripeOnboarding';
import Sell from './user/sell/Sell';
import SkinDb from './skin-db/database/SkinDb';
import ConnectAccount from './dashboard/profile/connect-account/ConnectAccount';
import TradeHistory from './dashboard/profile/trade-history/TradeHistory';
import OnSale from './user/on-sale/OnSale';
import Marketplace from './marketplace/Marketplace';
import Kyc from './dashboard/profile/Kyc';
import Contact from './footer/help/Contact';
import RefundPolicy from './legal/RefundPolicy';
import ConfirmMfa from './user/sign-in/ConfirmMfa';
import DashboardStats from './dashboard/system/dashboard/DashboardStats';
import SystemTrades from './dashboard/system/dashboard/SystemTrades';
import SystemBuyOrder from './dashboard/system/buyorder/SystemBuyOrder';
import UserBuyOrder from './dashboard/profile/buyorder/UserBuyOrder';
import Newsletter from './dashboard/system/newsletter/Newsletter';
import Premium from './dashboard/system/premium/Premium';
import UnsubscribePage from './user/UnsubscribePage';
import FaqDashboard from './faq/FaqDashboard';
import RolesAuthorities from "./dashboard/system/dashboard/RolesAuthorities";
import SystemMessageBanner from "./navbar/SystemMessageBanner";
import PaymentMethodSettings from "./dashboard/system/settings/PaymentMethodSettings";

const Pages = () => {
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
    const isMfaRequired = useSelector((state: RootState) => state.auth.isMfaRequired);
    const role = useSelector((state: RootState) => state.auth.role);
    const location = useLocation();
    const isVerifyRoute = location.pathname.startsWith('/verify');

    const {data, dismissedText} = useSelector((state: RootState) => state.systemMessage);

    const allowedAdminRoles = ['SYSTEM_ADMIN', 'ADMIN', 'SUPPORT'];
    const hasAdminAccess = role !== null && allowedAdminRoles.includes(role);

    // Guard for MFA: If MFA is required, redirect all attempts to /auth/mfa
    if (isMfaRequired && location.pathname !== '/auth/mfa') {
        return <Navigate to="/auth/mfa" replace/>;
    }

    // Handle verify route before rendering main layout
    if (!isAuthenticated && isVerifyRoute) {
        return <VerifyAndCompleteProfile/>;
    }

    // Hide navbars and footer if we are in MFA flow
    const showNav = !isMfaRequired;
    const showFooter = !isMfaRequired && location.pathname !== '/auth/mfa';

    // Check if system message banner is active and visible
    const isBannerVisible = showNav &&
        data !== null &&
        data.enabled === true &&
        Boolean(data.messageText) &&
        data.messageText.trim() !== '' &&
        dismissedText !== data.messageText;

    return (
        <>
            {/* Header container wrapping banner and navbars */}
            <div className="fixed top-0 left-0 right-0 z-50 flex flex-col">
                {showNav && <SystemMessageBanner/>}
                <div className="[&>*]:!static">
                    {isAuthenticated && showNav && <NavbarWeb/>}
                    {isAuthenticated && showNav && <NavbarMobile/>}
                    {!isAuthenticated && showNav && <NavBarNoAuthWeb/>}
                    {!isAuthenticated && showNav && <NavBarNoAuthMobile/>}
                </div>
            </div>

            {/* Render bottom mobile navigation outside of the static header container */}
            {isAuthenticated && showNav && <NavbarMobileBottom/>}

            <ScrollToTop/>

            {/* Dynamic top margin depending on banner visibility */}
            <div className={showNav ? (isBannerVisible ? 'mt-[105px] md:mt-[100px]' : 'mt-[65px] md:mt-[60px]') : ''}>
                <Routes>
                    {/* MFA Route */}
                    <Route path="/auth/mfa" element={<ConfirmMfa/>}/>

                    <Route path="/" element={<LandingPage/>}/>
                    <Route path="/tos" element={<Tos/>}/>
                    <Route path="/sign-in/mfa" element={<MfaVerificationForm/>}/>
                    <Route path="/privacy-policy" element={<PrivacyPolicy/>}/>
                    <Route path="/cookies" element={<CookiePolicy/>}/>
                    <Route path="/imprint" element={<Imprint/>}/>
                    <Route path="/refund-policy" element={<RefundPolicy/>}/>
                    <Route path="/contact" element={<Contact/>}/>
                    <Route path="/faq" element={<FaqDashboard/>}/>

                    <Route path="/system" element={isAuthenticated && hasAdminAccess ? <SystemLayout/> : <NotFound/>}>
                        <Route index element={<Navigate to="/system/dashboard"/>}/>
                        <Route path="dashboard" element={<DashboardStats/>}/>
                        <Route path="trades" element={<SystemTrades/>}/>
                        <Route path="buy-order" element={<SystemBuyOrder/>}/>
                        <Route path="transactions" element={<SystemTransactions/>}/>
                        <Route path="jobs" element={<Jobs/>}/>
                        <Route path="notifications" element={<Notification/>}/>
                        <Route path="settings" element={<Settings/>}/>
                        <Route path="payments" element={<PaymentMethodSettings/>}/>
                        <Route path="newsletter" element={<Newsletter/>}/>
                        <Route path="users" element={<UserList/>}/>
                        <Route path="roles-authorities" element={<RolesAuthorities/>}/>
                    </Route>

                    <Route path="/profile" element={isAuthenticated ? <ProfileLayout/> : <NotFound/>}>
                        <Route index element={<Navigate to="/profile/dashboard"/>}/>
                        <Route path="dashboard" element={<Dashboard/>}/>
                        <Route path="premium" element={<Premium/>}/>
                        <Route path="edit-profile" element={<EditProfile/>}/>
                        <Route path="security" element={<Security/>}/>
                        <Route path="trades">
                            <Route index element={<Navigate to="sold" replace/>}/>
                            <Route path="sold" element={<TradeHistory/>}/>
                            <Route path="purchased" element={<TradeHistory/>}/>
                        </Route>
                        <Route path="buy-order" element={<UserBuyOrder/>}/>
                        <Route path="transactions" element={<UserTransactions/>}/>
                        <Route path="connect-account" element={<ConnectAccount/>}/>
                        <Route path="kyc" element={<Kyc/>}/>
                        <Route path="stripe-onboarding" element={isAuthenticated ? <StripeOnboarding/> : <NotFound/>}/>
                        <Route path="stripe-onboarding/return" element={isAuthenticated ? <StripeOnboarding/> : <NotFound/>}/>
                        <Route path="notifications" element={<Notification/>}/>
                    </Route>

                    <Route path="/sell" element={isAuthenticated ? <Sell/> : <NotFound/>}/>
                    <Route path="/on-sale" element={isAuthenticated ? <OnSale/> : <NotFound/>}/>
                    <Route path="/market" element={<Marketplace/>}/>
                    <Route path="/market/offer/:offerId" element={<Marketplace/>}/>
                    <Route path="/database" element={<SkinDb/>}/>
                    <Route path="/cart" element={isAuthenticated ? <OnSale/> : <NotFound/>}/>
                    <Route path="/verify" element={!isAuthenticated ? <VerifyAndCompleteProfile/> : <NotFound/>}/>
                    <Route path="/newsletter/unsubscribe" element={<UnsubscribePage/>}/>

                    <Route path="*" element={<NotFound/>}/>
                </Routes>
            </div>
            {showFooter && <Footer/>}
        </>
    );
};

export default Pages;